// main.ts

import { createApp } from "vue";
import { plugin as formkitPlugin, defaultConfig } from "@formkit/vue";
import Toast from "vue-toastification";
import { createPinia } from "pinia";
import FloatingVue from 'floating-vue';
import { LDPlugin } from 'launchdarkly-vue-client-sdk';

import 'floating-vue/dist/style.css';
import "./style.css";
import App from "./App.vue";
import router from "./router/router";
import formkitConfig from "./formkit.config";
import { initializeSentry } from "@/plugins/sentryInitializer";
import { autoAnimatePlugin } from "@formkit/auto-animate/vue";
import { hamrsClientLogger } from "@/utils/loggers";
import { useSettingStore } from "@/stores/SettingStore";
import WatchlistMatchToast from "@/components/toasts/WatchlistMatchToast.vue";
import { pwaEnabled, LD_CLIENT_SIDE_ID } from "./config";
import { Capacitor } from "@capacitor/core";

import "leaflet/dist/leaflet.css";
import "@fontsource/rubik/300.css";
import "@fontsource/rubik/400.css";
import "@fontsource/rubik/500.css";
import "@fontsource/rubik/600.css";
import "@fontsource/rubik/700.css";
import "@fontsource/roboto-mono/300.css";
import "@fontsource/roboto-mono/400.css";
import "@fontsource/roboto-mono/500.css";
import "@fontsource/roboto-mono/600.css";
import "@fontsource/roboto-mono/700.css";
import "@fontsource/opendyslexic/400.css";
import "@fontsource/opendyslexic/700.css";
import "vue-toastification/dist/index.css";
import './cypressServiceInjector';
import { APP_VERSION } from './constants';

// Import the runMigrations function and services
import { runMigrations } from '@/migrations/migrationRunner';
import PotaParkService from '@/services/PotaParkService';
import OperatorService from '@/services/OperatorService';
import { useHunterStatsStore } from '@/stores/HunterStatsStore';
const ldContext = { kind: 'app', key: 'hamrs-vue-client' }
const ENVIRONMENT = import.meta.env.MODE;

hamrsClientLogger("Version:", APP_VERSION);
hamrsClientLogger("Environment:", ENVIRONMENT);
hamrsClientLogger("PWA Enabled:", pwaEnabled);

async function bootstrap() {
  try {
    // Request persistent storage for critical user data
    if ('storage' in navigator && 'persist' in navigator.storage) {
      try {
        const persistent = await navigator.storage.persist();
        hamrsClientLogger('Persistent storage granted:', persistent);
      } catch (error) {
        hamrsClientLogger('Failed to request persistent storage:', error);
      }
    }

    // Run migrations before initializing the app
    await runMigrations();

    // Create the app instance
    const app = createApp(App);
    const pinia = createPinia();

    app.use(pinia);
    
    // Start POTA parks data initialization in background (non-blocking)
    // This must be after Pinia is initialized since the repo uses stores
    // Error handling is done internally within initializeInBackground()
    hamrsClientLogger("Starting POTA parks data initialization in background...");
    PotaParkService.initializeInBackground();
    
    // Start operators data initialization in background (non-blocking)
    hamrsClientLogger("Starting operators data initialization in background...");
    OperatorService.initializeInBackground();

    // Start POTA hunter stats initialization in background (non-blocking): derives
    // per-park QSO counts from all logbooks and loads any imported pota.app CSV.
    hamrsClientLogger("Starting POTA hunter stats initialization in background...");
    useHunterStatsStore().initializeInBackground();

    // Now that Pinia is active, we can define toastOptions
    const toastOptions = {
      containerClassName: "hamrs-toast-container",
      filterBeforeCreate: (toast: typeof Toast) => {
        const settingStore = useSettingStore(); // Pinia is now active
        if (!settingStore.displayToasts) {
          // Watchlist alerts have their own independent toggle and should not
          // be suppressed by the call-lookup notifications setting.
          const content = (toast as any)?.content;
          if (content?.component === WatchlistMatchToast) {
            return toast;
          }
          return false;
        }
        return toast;
      },
    };

    app
      .use(formkitPlugin, defaultConfig(formkitConfig))
      .use(LDPlugin, { clientSideID: LD_CLIENT_SIDE_ID, context: ldContext })
      .use(router)
      .use(FloatingVue)
      .use(autoAnimatePlugin)
      .use(Toast, toastOptions);

    // Sentry must never block app bootstrap — the whole block is swallowed below
    // so telemetry is best-effort. All platforms report to the single `hamrs`
    // project; `environment` and `dist` distinguish them (see sentryInitializer).
    // VITE_SENTRY_DISABLED is set by capacitor:build:development so local debug
    // builds are fully Sentry-free (no runtime reporting and no map upload).
    const sentryDisabled = import.meta.env.VITE_SENTRY_DISABLED === "true";
    if (ENVIRONMENT != "development" && !sentryDisabled) {
      try {
        const native = Capacitor.isNativePlatform();
        // ios | android | electron | web
        const platform = native
          ? Capacitor.getPlatform()
          : (ENVIRONMENT.includes("electron") || /electron/i.test(navigator.userAgent)
              ? "electron"
              : "web");
        initializeSentry({
          app,
          router,
          appVersion: APP_VERSION,
          environment: platform,
          // iOS and Android ship the identical web JS bundle, so they share one
          // dist ('mobile'); environment still separates them at runtime.
          dist: native ? "mobile" : platform,
          native,
        });
      } catch (e) {
        hamrsClientLogger("Sentry init failed; continuing without it.", e);
      }
    }

    const settingStore = useSettingStore()
    settingStore.initDarkMode()
    settingStore.initOpenDyslexic()

    // Mount the app
    app.mount("#app");

    hamrsClientLogger("App initialized successfully.");

  } catch (error) {
    const err = error as Error;
    console.error('Application initialization failed:', err?.message, err?.stack, err);
  }
}

bootstrap();